<template>
  <v-card :disabled="loading || isDisabled" class="pt-0">
    <UserBadge :have-permissions="!isDisabled"/>
    <v-card-text class="pb-1 pt-5">
      <FlagSwitch
        class="dnd-switch"
        :loading="loading"
        :allow="!isDNDAllowed"
        :readonly="readOnlyDND"
        v-model="dnd"
        :label="$t('settings.dnd')"
        icon="minus-circle"
        help="dnd"
        icon-color="red"
      />
      <FlagSwitch
        class="missed-calls-switch"
        :loading="loading"
        v-model="showMissedCalls"
        :allow="!isMissedCallsAllowed"
        :readonly="readOnlyMissedCalls"
        :label="$t('settings.showMissedCalls')"
        icon="phone-missed"
        help="showMissedCalls"
      />
      <FlagSwitch
        class="cw-switch"
        :loading="loading"
        :allow="!isCWAllowed"
        :readonly="readOnlyCallWaiting"
        v-model="callWaiting"
        :label="$t('settings.callWaiting')"
        icon="phone-plus"
        help="callwait"
      />
      <FlagSwitch
        class="intercom-deskphone-switch"
        :loading="loading"
        v-show="isIcomAllowed"
        :allow="!isIcomAllowed"
        :readonly="readOnlyIntercom"
        v-model="icomDesk"
        :label="$t('settings.icomDesk')"
        icon="bullhorn"
        help="icomDesk"
      />
      <FlagSwitch
        class="intercom-callpanel-switch"
        :loading="loading"
        :allow="!isIcomAllowed"
        v-show="isIcomAllowed"
        :readonly="readOnlyIntercom"
        v-model="icomCallpanel"
        :label="$t('settings.icomCallpanel')"
        icon="bullhorn-outline"
        help="icomCallpanel"
      />
      <FlagSwitch
        class="intercom-anywhere-switch"
        :loading="loading"
        v-show="isIcomAllowed"
        :readonly="readOnlyIntercom"
        :allow="!isIcomAllowed"
        v-model="icomAnywhere"
        :label="$t('settings.icomAnywhere')"
        icon="bullhorn-outline"
        help="icomAnywhere"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import * as RMQClient from '@/rmqclient'
import { mapGetters, mapActions } from 'vuex'
import UserBadge from './user_badge'
import FlagSwitch from './flag_switch'

export default {
  name: 'settings-flags',
  components: { UserBadge, FlagSwitch },
  computed: {
    ...mapGetters({
      dndEnabled: 'settings/dnd',
      canReadDND: 'permissions/dndRead',
      canWriteDND: 'permissions/dndWrite',
      canWriteForDND: 'permissions/dndWriteFor',
      callWaitEnabled: 'settings/callwaiting',
      canReadCWT: 'permissions/cwtRead',
      canWriteCWT: 'permissions/cwtWrite',
      canWriteForCWT: 'permissions/cwtWriteFor',
      intercom: 'settings/intercom',
      canReadIcom: 'permissions/icomRead',
      canWriteIcom: 'permissions/icomWrite',
      canWriteIcomFor: 'permissions/icomWriteFor',
      canReadMissedCalls: 'permissions/userPreferencesRead',
      canWriteMissedCalls: 'permissions/userPreferencesWrite',
      showMissedCallsEnabled: 'settings/showMissedCalls',
      sipUser: 'sipua/exten',
      activeSubAccount: 'settings/activeSubAccount',
      isOwnerCurUser: 'settings/isOwnerCurUser',
      realm: 'settings/realm'
    }),
    isDisabled: {
      get: function () {
        return !(
          this.isDNDAllowed ||
          this.isMissedCallsAllowed ||
          this.isCWAllowed ||
          this.isIcomAllowed
        )
      }
    },
    dnd: {
      get: function () {
        return this.dndEnabled
      },
      set: function (enabled) {
        this.dndEnable(enabled)
      }
    },
    callWaiting: {
      get: function () {
        return this.callWaitEnabled
      },
      set: function (enabled) {
        this.cwEnable(enabled)
      }
    },
    icomDesk: {
      get: function () {
        return this.intercom.deskphone
      },
      set: function (enabled) {
        this.icomEnable('deskphone', enabled)
      }
    },
    icomCallpanel: {
      get: function () {
        return this.intercom.callpanel
      },
      set: function (enabled) {
        this.icomEnable('callpanel', enabled)
      }
    },
    icomAnywhere: {
      get: function () {
        return Boolean(this.intercom.anywhere)
      },
      set: function (enabled) {
        this.icomEnable('anywhere', enabled)
      }
    },
    showMissedCalls: {
      get: function () {
        return this.showMissedCallsEnabled
      },
      set: function (enabled) {
        this.setShowMissedCalls(enabled)
      }
    },
    isMissedCallsAllowed: function () {
      return this.isFeatureAllowed(
        'preferences',
        this.canReadMissedCalls,
        this.canWriteMissedCalls
      )
    },
    isDNDAllowed: function () {
      return this.isFeatureAllowed(
        'do_not_disturb',
        this.canReadDND,
        this.canWriteDND
      )
    },
    isCWAllowed: function () {
      return this.isFeatureAllowed(
        'call_waiting',
        this.canReadCWT,
        this.canWriteCWT
      )
    },
    isIcomAllowed: function () {
      return this.isFeatureAllowed(
        'intercom',
        this.canReadIcom,
        this.canWriteIcom
      )
    },
    isDNDWriteAllowed: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return this.canWriteDND
      }
      return this.canWriteForDND(this.activeSubAccount?.subscriber_id)
    },
    isCallWaitingWriteAllowed: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return this.canWriteCWT
      }
      return this.canWriteForCWT(this.activeSubAccount?.subscriber_id)
    },
    isIcomWriteAllowed: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return this.canWriteIcom
      }
      return this.canWriteIcomFor(this.activeSubAccount?.subscriber_id)
    },
    isWriteMissedCallsAllowed: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return this.canWriteMissedCalls
      }
      // no _for permissions for now
      return false
    },
    readOnlyDND: function () {
      return this.isDNDAllowed && !this.isDNDWriteAllowed
    },
    readOnlyMissedCalls: function () {
      return this.isMissedCallsAllowed && !this.isWriteMissedCallsAllowed
    },
    readOnlyCallWaiting: function () {
      return this.isCWAllowed && !this.isCallWaitingWriteAllowed
    },
    readOnlyIntercom: function () {
      return this.isIcomAllowed && !this.isIcomWriteAllowed
    }
  },
  data () {
    return {
      loading: false
    }
  },
  methods: {
    ...mapActions({
      setDND: 'settings/setDND',
      setCallwaiting: 'settings/setCallwaiting',
      setIntercom: 'settings/setIntercom',
      setShowMissedCalls: 'settings/setShowMissedCalls'
    }),
    icomEnable: function (icomType, enabled) {
      this.loading = true
      this.setIntercom({ enabled: enabled, icom: icomType }).finally(() => {
        this.loading = false
      })
    },
    dndEnable: function (enabled) {
      this.loading = true
      this.setDND(enabled)
        .then(() => {
          RMQClient.sendCmd({
            route: 'DIRECT',
            action: 'BUDDYPREF',
            pref: 'DND',
            username: this.sipUser,
            realm: this.realm,
            enabled: enabled
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    cwEnable: function (enabled) {
      this.loading = true
      this.setCallwaiting(enabled).finally(() => {
        this.loading = false
      })
    },
    isFeatureAllowed: function (feature, rootRead, rootWrite) {
      if (this.activeSubAccount && !this.isOwnerCurUser) {
        return this.activeSubAccount.features?.includes(feature)
      }
      return rootRead && rootWrite
    }
  }
}
</script>

<style scoped></style>
